import { find } from 'lodash';

import {
  createPropertyDraft,
  updatePropertyDraft,
  fetchPropertyDraftContractByToken,
  savePropContractInCache,
} from '../../api/contractApi';

import {
  convertPropertyContractToStoreFormat,
  convertSignedPropertyContractToStoreFormat,
  mapErrorsToContractSteps,
} from '../../helpers/propertyContractHelpers';

import { propertyCalculationReducerForContract } from './propertyCalculator';
import { PROPERTY_EXTRA_RISKS } from '../../constants/propertyCalcConstants';
import { PAYMENT_FREQUENCIES, SINGLE } from '../../constants/paymentFrequency';

const SET_SIGNER = 'efes/propertyContract/SET_SIGNER';
const SET_HAS_ADD_SIGNER = 'efes/propertyContract/SET_HAS_ADD_SIGNER';

const ADD_OWNER = 'efes/propertyContract/ADD_OWNER';
const SAVE_OWNER = 'efes/propertyContract/SAVE_OWNER';
const REMOVE_OWNER = 'efes/propertyContract/REMOVE_OWNER';

const SET_PROPERTY_DATA = 'efes/propertyContract/SET_PROPERTY_DATA';
const SET_IS_SAME_ADDRESS = 'efes/propertyContract/SET_IS_SAME_ADDRESS';
const SET_PROPERTY_DETAILS = 'efes/propertyContract/SET_PROPERTY_DETAILS';

const SET_PAYMENT_METHOD = 'efes/propertyContract/SET_PAYMENT_METHOD';
const SET_HAS_AGREED = 'efes/propertyContract/SET_HAS_AGREED';

const SAVE_DRAFT_CONTRACT = 'efes/propertyContract/SAVE_DRAFT_CONTRACT';
const SAVE_DRAFT_CONTRACT_SUCCESS = 'efes/propertyContract/SAVE_DRAFT_CONTRACT_SUCCESS';
const SAVE_DRAFT_CONTRACT_FAIL = 'efes/propertyContract/SAVE_DRAFT_CONTRACT_FAIL';

const SAVE_PROPERTY_CONTRACT_IN_CACHE = 'efes/propertyContract/SAVE_PROPERTY_CONTRACT_IN_CACHE';
const SAVE_PROPERTY_CONTRACT_IN_CACHE_SUCCESS = 'efes/propertyContract/SAVE_PROPERTY_CONTRACT_IN_CACHE_SUCCESS';
const SAVE_PROPERTY_CONTRACT_IN_CACHE_FAIL = 'efes/propertyContract/SAVE_PROPERTY_CONTRACT_IN_CACHE_FAIL';

const CONTINUE_FROM_DRAFT = 'efes/propertyContract/CONTINUE_FROM_DRAFT';
const LOAD_SIGNED_CONTRACT = 'efes/propertyContract/LOAD_SIGNED_CONTRACT';

const GET_DRAFT_PROPERTY_CONTRACT = 'efes/propertyContract/GET_DRAFT_PROPERTY_CONTRACT';
const GET_DRAFT_PROPERTY_CONTRACT_SUCCESS = 'efes/propertyContract/GET_DRAFT_PROPERTY_CONTRACT_SUCCESS';
const GET_DRAFT_PROPERTY_CONTRACT_FAIL = 'efes/propertyContract/GET_DRAFT_PROPERTY_CONTRACT_FAIL';

const CLEAR_PROPERTY_CONTRACT = 'efes/propertyContract/CLEAR_PROPERTY_CONTRACT';

const CLEAR_SIGNER_ERRORS = 'efes/propertyContract/CLEAR_SIGNER_ERRORS';
const REMOVE_OWNER_ERRORS = 'efes/propertyContract/REMOVE_OWNER_ERRORS';
const CLEAR_CALCULATOR_ERRORS = 'efes/propertyContract/CLEAR_CALCULATOR_ERRORS';

const getEmptyAmounts = () => ({
  stylingAndEngineeringEquipment: null,
  movableEstate: null,
  bearingStructures: null,
  civilLiability: null,
});

const getEmptyPrices = () => ({
  stylingAndEngineeringEquipment: null,
  movableEstate: null,
  bearingStructures: null,
  civilLiability: null,
});

const initialState = {
  contractData: {
    draftId: '',
    hasAddedSigner: true,
    signer: {},
    owners: [],
    propertyData: {
      isSameAddress: true,
    },
    propertyDetails: {
      propertyType: null,
      usePurpose: null,
      propertyArea: '',
      paymentFrequency: PAYMENT_FREQUENCIES[SINGLE],
      range: {
        start: null,
        end: null,
      },
      amounts: getEmptyAmounts(),
      prices: getEmptyPrices(),
      isFetchingPrice: false,
      priceErrorCode: '',
      extraRisks: PROPERTY_EXTRA_RISKS,
      paymentFrequencies: {},
      paymentsSchedule: {},
    },
  },
  hasAgreed: false,
  paymentMethod: '',
  isGettingPrices: false,
  loadingDraftContract: false,
  draftToken: null,
  orderId: null,
  savingContractInCache: false,
  signerBackendErrors: null,
  ownerBackendErrors: null,
  calculatorBackendErrors: null,
};

const saveOwnerReducer = (owners, newOwner) => owners.map((owner) => {
  if (owner.memberId === newOwner.memberId) {
    return newOwner;
  }
  return owner;
});

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_SIGNER:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          signer: action.data,
        },
      };
    case SET_HAS_ADD_SIGNER:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          hasAddedSigner: action.data,
        },
      };
    case ADD_OWNER:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          owners: [...state.contractData.owners, action.data],
        },
      };

    case SAVE_OWNER:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          owners:
            saveOwnerReducer(
              state.contractData.owners,
              action.data,
            ),
        },
      };
    case REMOVE_OWNER:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          owners: state.contractData.owners.filter(
            (t) => t.memberId !== action.data.memberId,
          ),
        },
      };
    case SET_PROPERTY_DATA:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          propertyData: action.data,
        },
      };
    case SET_IS_SAME_ADDRESS:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          propertyData: { ...state.contractData.propertyData, isSameAddress: action.data },
        },
      };
    case SET_PAYMENT_METHOD:
      return {
        ...state,
        paymentMethod: action.data,
      };
    case SET_HAS_AGREED:
      return {
        ...state,
        hasAgreed: action.data,
      };
    case SAVE_DRAFT_CONTRACT_SUCCESS:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          draftId: action.result,
        },
      };
    case SAVE_PROPERTY_CONTRACT_IN_CACHE:
      return {
        ...state,
        draftToken: null,
        orderId: null,
        savingContractInCache: true,
      };
    case SAVE_PROPERTY_CONTRACT_IN_CACHE_SUCCESS:
      return {
        ...state,
        draftToken: action.result.draftToken,
        orderId: action.result.id,
        savingContractInCache: false,
      };
    case SAVE_PROPERTY_CONTRACT_IN_CACHE_FAIL:
      return {
        ...state,
        draftToken: null,
        orderId: null,
        savingContractInCache: false,
        ...action.backendErrors,
      };
    case SET_PROPERTY_DETAILS: {
      return {
        ...state,
        contractData: {
          ...state.contractData,
          propertyDetails: { ...state.contractData.propertyDetails, ...action.data },
        },
      };
    }
    case GET_DRAFT_PROPERTY_CONTRACT:
      return {
        ...state,
        loadingDraftContract: true,
      };
    case GET_DRAFT_PROPERTY_CONTRACT_SUCCESS:
      return {
        ...state,
        contractData: { ...state.contractData, ...action.result },
        loadingDraftContract: false,
      };
    case GET_DRAFT_PROPERTY_CONTRACT_FAIL:
      return {
        ...state,
        loadingDraftContract: false,
      };

    case LOAD_SIGNED_CONTRACT:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          ...action.data,
          propertyData: {
            ...state.contractData.propertyData,
            ...action.data.propertyData,
          },
        },
      };

    case CONTINUE_FROM_DRAFT:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          ...action.data,
          propertyData: {
            ...state.contractData.propertyData,
            ...action.data.propertyData,
          },
        },
      };
    case CLEAR_SIGNER_ERRORS:
      return {
        ...state,
        signerBackendErrors: null,
      };
    case REMOVE_OWNER_ERRORS: {
      const newErrors = { ...state.ownerBackendErrors };
      delete newErrors[action.memberId];
      return {
        ...state,
        ownerBackendErrors: newErrors,
      };
    }
    case CLEAR_CALCULATOR_ERRORS:
      return {
        ...state,
        calculatorBackendErrors: null,
      };
    case CLEAR_PROPERTY_CONTRACT:
    default:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          propertyDetails: propertyCalculationReducerForContract(
            state.contractData.propertyDetails,
            action,
          ),
        },
      };
  }
}

export const saveDraftContract = () => (dispatch, getStore) => {
  const { contractData } = getStore().propertyContract;
  const { draftId } = contractData;
  return dispatch({
    actions: [
      { type: SAVE_DRAFT_CONTRACT },
      { type: SAVE_DRAFT_CONTRACT_SUCCESS },
      { type: SAVE_DRAFT_CONTRACT_FAIL },
    ],
    promise: draftId
      ? updatePropertyDraft(contractData)
      : createPropertyDraft(contractData),
  });
};

export function savePropertyContractInCache() {
  return (dispatch, getStore) => {
    const { contractData } = getStore().propertyContract;
    dispatch({ type: SAVE_PROPERTY_CONTRACT_IN_CACHE });
    return savePropContractInCache(contractData)
      .then((result) => {
        dispatch({ type: SAVE_PROPERTY_CONTRACT_IN_CACHE_SUCCESS, result });
        return result;
      })
      .catch((error) => {
        const backendErrors = mapErrorsToContractSteps(
          error.response.data.errors,
          contractData,
        );

        dispatch({
          type: SAVE_PROPERTY_CONTRACT_IN_CACHE_FAIL,
          showError: true,
          customMsg: 'contactAgent',
          backendErrors,
        });
        return Promise.reject();
      });
  };
}

export const setSigner = (data) => ({ type: SET_SIGNER, data });
export const setHasAddedSigner = (data) => ({ type: SET_HAS_ADD_SIGNER, data });
export const addOwner = (data) => ({ type: ADD_OWNER, data });
export const saveOwner = (data) => ({ type: SAVE_OWNER, data });

export const removeOwner = (data) => ({ type: REMOVE_OWNER, data });

export const setPropertyData = (data) => ({ type: SET_PROPERTY_DATA, data });
export const setIsSameAddress = (data) => ({ type: SET_IS_SAME_ADDRESS, data });

export const setPaymentMethod = (data) => ({ type: SET_PAYMENT_METHOD, data });

export const setPropertyDetails = (data) => ({ type: SET_PROPERTY_DETAILS, data });

export const setHasAgreed = (data) => ({ type: SET_HAS_AGREED, data });

export const clearPropertyContract = () => ({ type: CLEAR_PROPERTY_CONTRACT });

export function getDraftPropertyContract(id, token) {
  return {
    actions: [
      { type: GET_DRAFT_PROPERTY_CONTRACT },
      { type: GET_DRAFT_PROPERTY_CONTRACT_SUCCESS },
      { type: GET_DRAFT_PROPERTY_CONTRACT_FAIL },
    ],
    promise: fetchPropertyDraftContractByToken(id, token),
  };
}

export function getDraftPropertyContractAndSaveInCache(id, token) {
  return (dispatch) => dispatch(getDraftPropertyContract(id, token))
    .then(() => dispatch(savePropertyContractInCache()));
}

export const continueFromDraft = (draft) => {
  const contract = convertPropertyContractToStoreFormat(draft);
  return {
    type: CONTINUE_FROM_DRAFT,
    data: contract,
  };
};

export const loadSignedContract = (contract) => {
  const convertedContract = convertSignedPropertyContractToStoreFormat(contract);
  return {
    type: LOAD_SIGNED_CONTRACT,
    data: convertedContract,
  };
};

export function setOrUpdateSigner(signerData) {
  return (dispatch, getState) => {
    const { signer, owners } = getState().propertyContract.contractData;
    const isSignerOwner = find(
      owners,
      ({ memberId }) => memberId === signer.memberId,
    );
    if (isSignerOwner) {
      dispatch(
        saveOwner({
          ...isSignerOwner,
          ...signerData,
        }),
      );
    }
    dispatch(setSigner(signerData));
  };
}

export function setOrUpdatePropertyData(propertyData) {
  return (dispatch) => {
    dispatch(setPropertyData(propertyData));
  };
}

export const clearSignerBackendErrors = () => ({ type: CLEAR_SIGNER_ERRORS });
export const removeOwnerBackendErrors = (memberId) => ({
  type: REMOVE_OWNER_ERRORS,
  memberId,
});
export const clearCalculationBackendErrors = () => ({
  type: CLEAR_CALCULATOR_ERRORS,
});
